import React, { useEffect, useMemo, useState } from "react";
import { Table, Select, Spin, Tag, Skeleton } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getFacebookLeadForms,
  getFacebookFormLeads,
} from "../features/customers/customerSlice";
import "../styles/leads.css";

const FacebookLeadsPage = () => {
  const dispatch = useDispatch();
  const [selectedFormId, setSelectedFormId] = useState("");
  const [leadStatuses, setLeadStatuses] = useState({});

  const {
    forms = [],
    formLeads = [],
    isLoading,
  } = useSelector((state) => state.customer);

  useEffect(() => {
    dispatch(getFacebookLeadForms());
  }, [dispatch]);

  const activeForms = useMemo(() => {
    return forms.filter((form) => form.status === "ACTIVE");
  }, [forms]);

  useEffect(() => {
    if (activeForms.length > 0 && !selectedFormId) {
      const firstActiveForm = activeForms[0];

      if (firstActiveForm?.id) {
        setSelectedFormId(firstActiveForm.id);
        dispatch(getFacebookFormLeads(firstActiveForm.id));
      }
    }
  }, [activeForms, selectedFormId, dispatch]);

  useEffect(() => {
    if (formLeads.length > 0) {
      const nextStatuses = {};
      formLeads.forEach((lead) => {
        nextStatuses[lead.leadgenId] = leadStatuses[lead.leadgenId] || "new";
      });
      setLeadStatuses(nextStatuses);
    }
  }, [formLeads]);

  const handleFormChange = (value) => {
    setSelectedFormId(value);
    dispatch(getFacebookFormLeads(value));
  };

  const handleStatusChange = (leadgenId, value) => {
    setLeadStatuses((prev) => ({
      ...prev,
      [leadgenId]: value,
    }));
  };

const truncateNameToTwoWords = (name) => {
  if (!name || typeof name !== "string") return "Not provided";

  // split by space OR dot
  const parts = name.split(/[\s.]+/).filter(Boolean);

  if (parts.length <= 2) {
    return name;
  }

  return `${parts.slice(0, 2).join(" ")}...`;
};

const dataSource = useMemo(() => {
  return formLeads.map((lead, index) => ({
    key: lead.leadgenId || index + 1,
    leadgenId: lead.leadgenId || "",
    sno: index + 1,
    name: truncateNameToTwoWords(lead.name),
    fullName: lead.name || "Not provided",
    phone: lead.phone || "Not provided",
    assignedTo: "Team",
    status: leadStatuses[lead.leadgenId] || "new",
    date: lead.createdTime
      ? new Date(lead.createdTime).toLocaleDateString()
      : "Not available",
  }));
}, [formLeads, leadStatuses]);

  const columns = [
    { title: "SNo", dataIndex: "sno", width: 70 },
      {
      title: "Name",
      dataIndex: "name",
      render: (_, record) => (
        <span title={record.fullName}>{record.name}</span>
      ),
    },
    { title: "Phone", dataIndex: "phone" },
    { title: "Assigned To", dataIndex: "assignedTo" },
    {
      title: "Status",
      render: (_, record) => (
        <Select
          size="small"
          value={record.status}
          style={{ width: 140 }}
          onChange={(value) => handleStatusChange(record.leadgenId, value)}
          options={[
            { value: "new", label: "New" },
            { value: "contacted", label: "Contacted" },
          ]}
        />
      ),
    },
    { title: "Date", dataIndex: "date" },
  ];

  const formOptions = activeForms.map((form) => ({
    value: form.id,
    label: (
      <div className="d-flex justify-content-between align-items-center w-100">
        <span>{form.name}</span>
        <Tag
          color="green"
          style={{
            fontSize: "11px",
            lineHeight: "16px",
            marginRight: 0,
          }}
        >
          {form.status}
        </Tag>
      </div>
    ),
  }));

 

  const selectedForm = activeForms.find((form) => form.id === selectedFormId);

const renderKpiSkeletons = () => (
  <div className="mb-4 leads__count-wrapper">
    {Array.from({ length: 3 }).map((_, index) => (
      <div className="col-md-3 d-flex" key={index}>
        <div
          className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            minHeight: 22,
            justifyContent: "center",
          }}
        >
          <Skeleton.Input active size="small" style={{ width: 120, height:15 }} />
          <Skeleton.Input active size="default" style={{ width: 60 , height:25}} />
        </div>
      </div>
    ))}
  </div>
);

const hasData =
  activeForms.length > 0 || formLeads.length > 0;

const displayNumber = (value) => {
  if (!hasData) return "0";
  return value;
};

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-1 title">Facebook Leads</h3>
          <p className="text-muted mb-0">
            {selectedForm
              ? `Showing leads for: ${selectedForm.name}`
              : "Select a form to view its leads"}
          </p>
        </div>
      </div>

      <div className="d-flex gap-3 mb-3 flex-wrap">
        <Select
          placeholder="Select Active Facebook Lead Form"
          style={{ width: 420 }}
          value={selectedFormId || undefined}
          onChange={handleFormChange}
          options={formOptions}
        />
      </div>

{isLoading && !hasData ? (
  renderKpiSkeletons()
) : (
  <div className="mb-4 leads__count-wrapper">
    <div className="col-md-3 d-flex">
      <div
        className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
        style={{ borderLeft: "5px solid #4f46e5" }}
      >
        <small style={{ color: "#4f46e5" }}>Active Forms</small>
        <h4 className="mb-0" style={{ color: "#1e1b4b" }}>
          {displayNumber(activeForms.length)}
        </h4>
      </div>
    </div>

    <div className="col-md-3 d-flex">
      <div
        className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
        style={{ borderLeft: "5px solid #0891b2" }}
      >
        <small style={{ color: "#0891b2" }}>
          Leads in Selected Form
        </small>
        <h4 className="mb-0" style={{ color: "#164e63" }}>
          {displayNumber(formLeads.length)}
        </h4>
      </div>
    </div>

    <div className="col-md-3 d-flex">
      <div
        className="bg-white p-3 rounded-3 flex-grow-1 shadow-sm"
        style={{ borderLeft: "5px solid #16a34a" }}
      >
        <small style={{ color: "#16a34a" }}>Selected Status</small>
        <h4 className="mb-0" style={{ color: "#14532d", fontSize: "20px" }}>
          {selectedForm?.status || "—"}
        </h4>
      </div>
    </div>
  </div>
)}

      <div className="bg-white p-4 rounded-3">
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 900 }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default FacebookLeadsPage;